// « Envoyer et ranger » : envoyer le brouillon, puis ranger LA COPIE ENVOYÉE.
//
// POURQUOI CET ORDRE, ET PAS « ranger puis envoyer ». Le rangement déduplique sur
// le Message-ID RFC (`outlook.mails.message_id_rfc`). Or le Message-ID d'un
// brouillon CHANGE à l'envoi sur Outlook classique Windows — bug produit
// reconnu par Microsoft (OfficeDev/office-js #2659, #6066) — alors qu'il reste
// stable sur Outlook web et le nouvel Outlook. Archiver le brouillon puis
// envoyer produirait donc, sur ce client, une entrée dont l'identifiant ne
// correspond à rien de ce qui est parti : quiconque rangerait ensuite la copie
// des Éléments envoyés créerait un doublon que la déduplication ne verrait pas.
//
// S'ajoute à cela que le MIME d'un brouillon n'a ni `Date:` définitif ni `From:`
// final (Outlook le synthétise à la lecture ; les en-têtes de transport sont
// apposés à la soumission). L'archive aurait donc une colonne « Envoyé le » vide.
//
// En envoyant d'abord, on archive un .eml FIDÈLE à ce qui est réellement parti,
// et il n'existe qu'un seul exemplaire archivé. Le risque résiduel est inverse et
// préférable : si le rangement échoue, le mail est parti mais non rangé — visible,
// et rattrapable à la main depuis les Éléments envoyés.
//
// COMMENT ON RETROUVE LA COPIE ENVOYÉE. Ni l'`id` ni le Message-ID ne survivent
// de façon fiable au passage Brouillons -> Éléments envoyés (« this value changes
// when the item is moved from one container to another »). On appose donc AVANT
// l'envoi notre propre clé, comme propriété étendue nommée : elle voyage avec le
// message, survit au déplacement, et — contrairement à `internetMessageHeaders` —
// est filtrable en `$filter`. L'alternative documentée (identifiants immuables
// via `translateExchangeIds`) aurait exigé un scope `User.ReadBasic.All`, soit la
// lecture du profil de TOUS les utilisateurs du tenant pour envoyer un mail.
import { acquireMailToken } from '../auth/msalClient';
import { addBreadcrumb } from '../breadcrumbs/breadcrumbs';

const GRAPH = 'https://graph.microsoft.com/v1.0';

// GUID FIXE de notre jeu de propriétés étendues. Il doit rester stable : c'est la
// moitié du nom sous lequel la clé est écrite puis relue.
const PROP_SET_GUID = '{2f0b9a3e-7c41-4d8f-9b6a-5e3d1c74af20}';
const ARCHIVE_KEY_PROP = `String ${PROP_SET_GUID} Name PrismArchiveKey`;

async function graph(
  path: string,
  token: string,
  init: RequestInit = {},
): Promise<Response> {
  return fetch(`${GRAPH}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
}

async function failure(res: Response, what: string): Promise<Error> {
  let detail = '';
  try {
    detail = (await res.text()).slice(0, 400);
  } catch {
    // Corps illisible : le statut seul devra suffire au diagnostic.
  }
  return new Error(`${what}: HTTP ${res.status} ${detail}`);
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Identifiant REST du brouillon courant.
 *
 * `getItemIdAsync` (Mailbox 1.8) est préféré à `saveAsync` : il rend l'id d'un
 * élément DÉJÀ enregistré sans le réenregistrer. Il lève `ItemNotSaved` sur un
 * brouillon jamais sauvegardé — c'est le seul cas où l'on écrit réellement.
 *
 * La conversion par `convertToRestId` est OBLIGATOIRE : Office rend un
 * identifiant EWS, « not identical to the Outlook Entry ID or the ID used by the
 * Outlook REST API ».
 */
export async function getComposeRestId(): Promise<string> {
  const item = Office.context.mailbox.item as unknown as {
    getItemIdAsync?: (cb: (r: Office.AsyncResult<string>) => void) => void;
    saveAsync?: (cb: (r: Office.AsyncResult<string>) => void) => void;
  };

  const ewsId = await new Promise<string>((resolve, reject) => {
    const viaSave = () => {
      if (!item.saveAsync) return reject(new Error('saveAsync unavailable'));
      item.saveAsync((r) => {
        if (r.status === Office.AsyncResultStatus.Succeeded) resolve(r.value);
        else reject(new Error(r.error?.message ?? 'saveAsync failed'));
      });
    };
    if (!item.getItemIdAsync) return viaSave();
    item.getItemIdAsync((r) => {
      if (r.status === Office.AsyncResultStatus.Succeeded) resolve(r.value);
      // Brouillon encore jamais enregistré : on l'enregistre, ce qui lui donne
      // un id.
      else viaSave();
    });
  });

  return Office.context.mailbox.convertToRestId(
    ewsId,
    Office.MailboxEnums.RestVersion.v2_0,
  );
}

/**
 * Appose notre clé d'archivage sur le brouillon.
 *
 * Réessaie, car en mode CACHE (le défaut d'Outlook classique) le brouillon
 * n'est pas encore synchronisé au serveur quand Office rend son id : « until the
 * item is synced, using the item ID will return an error ». Un 404 ici est donc
 * normal pendant une seconde ou deux, et non une erreur définitive.
 */
export async function stampArchiveKey(restId: string, token: string): Promise<string> {
  const key = crypto.randomUUID();
  const body = JSON.stringify({
    singleValueExtendedProperties: [{ id: ARCHIVE_KEY_PROP, value: key }],
  });

  let lastErr: Error | null = null;
  for (let attempt = 0; attempt < 6; attempt++) {
    const res = await graph(`/me/messages/${restId}`, token, { method: 'PATCH', body });
    if (res.ok) return key;
    // 404 / 400 = brouillon pas encore synchronisé ; on laisse le temps.
    if (res.status !== 404 && res.status !== 400) throw await failure(res, 'stamp archive key');
    lastErr = await failure(res, 'stamp archive key');
    await sleep(400 * (attempt + 1));
  }
  throw lastErr ?? new Error('stamp archive key: brouillon non synchronisé');
}

/** Envoie le brouillon. Graph répond 202 Accepted, SANS corps. */
export async function sendDraft(restId: string, token: string): Promise<void> {
  const res = await graph(`/me/messages/${restId}/send`, token, { method: 'POST' });
  if (!res.ok) throw await failure(res, 'send draft');
}

/**
 * Échec survenu APRÈS que le mail est parti.
 *
 * LA DISTINCTION N'EST PAS COSMÉTIQUE : « pas envoyé » et « envoyé mais pas
 * rangé » appellent deux gestes de rattrapage OPPOSÉS — renvoyer dans le premier
 * cas, surtout pas dans le second, où il faut aller ranger la copie depuis les
 * Éléments envoyés. Les confondre pousserait l'utilisateur à envoyer deux fois le
 * même mail à un client.
 */
export class AfterSendError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'AfterSendError';
  }
}

export interface SentCopy {
  id: string;
  subject: string;
  internetMessageId: string | null;
  conversationId: string | null;
  sentDateTime: string | null;
  fromName: string | null;
  fromAddress: string | null;
}

interface GraphSentRow {
  id: string;
  subject?: string | null;
  internetMessageId?: string | null;
  conversationId?: string | null;
  sentDateTime?: string | null;
  from?: { emailAddress?: { name?: string | null; address?: string | null } | null } | null;
}

/**
 * Retrouve dans les Éléments envoyés le message portant notre clé.
 *
 * SONDAGE OBLIGATOIRE, pas un luxe : `/send` répond 202 sans rien dire du
 * résultat, et Microsoft prévient que « getting the message in Sent Items might
 * not succeed immediately after sending [...] The copy of the message is not
 * created until the message successfully sends, which might take time ». En mode
 * cache s'y ajoute le délai de synchronisation. Une absence n'est donc pas un
 * échec tant que le délai n'est pas épuisé.
 */
export async function findSentCopy(
  key: string,
  token: string,
  { attempts = 10, delayMs = 900 }: { attempts?: number; delayMs?: number } = {},
): Promise<SentCopy> {
  const filter =
    `singleValueExtendedProperties/any(ep:ep/id eq '${ARCHIVE_KEY_PROP}' and ep/value eq '${key}')`;
  const path =
    `/me/mailFolders/SentItems/messages?$filter=${encodeURIComponent(filter)}` +
    `&$select=id,subject,internetMessageId,conversationId,sentDateTime,from&$top=1`;

  for (let attempt = 0; attempt < attempts; attempt++) {
    const res = await graph(path, token);
    if (res.ok) {
      const data = (await res.json()) as { value?: GraphSentRow[] };
      const row = data.value?.[0];
      if (row) {
        return {
          id: row.id,
          subject: row.subject ?? '',
          internetMessageId: row.internetMessageId ?? null,
          conversationId: row.conversationId ?? null,
          sentDateTime: row.sentDateTime ?? null,
          fromName: row.from?.emailAddress?.name ?? null,
          fromAddress: row.from?.emailAddress?.address ?? null,
        };
      }
    } else if (res.status !== 404) {
      throw await failure(res, 'find sent copy');
    }
    addBreadcrumb({
      category: 'mutation',
      message: 'sent copy not there yet',
      data: { attempt },
    });
    await sleep(delayMs);
  }
  throw new Error(
    "le mail a bien été envoyé, mais sa copie n'est pas encore visible dans les " +
      'Éléments envoyés : il faudra le ranger à la main',
  );
}

/** MIME complet (.eml) d'un message, en base64 — la forme attendue par file-mail. */
export async function fetchMimeBase64(id: string, token: string): Promise<string> {
  const res = await graph(`/me/messages/${id}/$value`, token);
  if (!res.ok) throw await failure(res, 'fetch mime');
  const bytes = new Uint8Array(await res.arrayBuffer());
  // Par tranches : `String.fromCharCode(...bytes)` dépasse la taille d'argument
  // maximale sur un mail avec pièces jointes et lève un RangeError.
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/**
 * Déplace un message vers les Éléments supprimés.
 *
 * `DELETE /me/messages/{id}` en Graph n'est PAS une purge : le message part dans
 * les Éléments supprimés, d'où l'utilisateur peut le récupérer — c'est la
 * suppression normale d'Outlook. Aucune API Office.js ne le permet, et
 * `makeEwsRequestAsync` n'est plus une option (jetons Exchange Online
 * historiques coupés sur tous les tenants M365).
 */
export async function moveMessageToDeletedItems(restId: string, token: string): Promise<void> {
  const res = await graph(`/me/messages/${restId}`, token, { method: 'DELETE' });
  if (!res.ok) throw await failure(res, 'delete message');
}

export interface SendAndFileHandle {
  token: string;
  sent: SentCopy;
  mimeBase64: string;
}

/**
 * Enchaîne : clé -> envoi -> localisation de la copie envoyée -> MIME.
 *
 * S'arrête au premier échec, et le TYPE de l'erreur dit si le mail est parti :
 * une `AfterSendError` signifie « envoyé, mais pas capturé », tout le reste
 * signifie « rien n'est parti ». `sendDraft` est la frontière, et elle est ici
 * la seule chose qui compte pour l'appelant.
 */
export async function sendThenCapture(): Promise<SendAndFileHandle> {
  // AVANT l'envoi : un échec ici est sans conséquence, le brouillon est intact.
  const token = await acquireMailToken();
  const restId = await getComposeRestId();
  const key = await stampArchiveKey(restId, token);

  await sendDraft(restId, token);
  addBreadcrumb({ category: 'mutation', message: 'draft sent, locating sent copy' });

  // APRÈS l'envoi : le mail est chez le destinataire, plus aucun échec n'est
  // rattrapable par un renvoi.
  try {
    const sent = await findSentCopy(key, token);
    const mimeBase64 = await fetchMimeBase64(sent.id, token);
    return { token, sent, mimeBase64 };
  } catch (err) {
    throw new AfterSendError(err instanceof Error ? err.message : String(err), {
      cause: err,
    });
  }
}
